import { IFormatStringResult } from "../entities/format-string-result";
import { ObjectMap } from "../entities/object-map";
import { forEach } from "./collection";

function isObject(obj:any): obj is {} {
    return !!obj && typeof obj === 'object';
}

function deepCloneObject<T>(obj: ObjectMap<T>): ObjectMap<T> | null | undefined {
    if(!obj) { return null; }
    const copy: ObjectMap = {};

    const cb = (item: any, key: string) => {
        copy[key] = isObject(item) ? deepClone(item) : item;
    };

    for(const key in obj) {
        if(obj.hasOwnProperty(key)) {
            cb(obj[key], key);
        }
    }

    return copy;
}

export function deepClone<T>(obj: T[] | ObjectMap<T>): T[] | ObjectMap<T> | null | undefined {
    if(Array.isArray(obj)) {
        let arr = obj as any[];
        return arr.map(item => item);
    } else {
        return deepCloneObject(obj);
    }
}

export function contentChanged(original:any, workingCopy:any):boolean {
    var txtOrig = JSON.stringify(original);
    let txtCopy = JSON.stringify(workingCopy);

    const changed = txtOrig !== txtCopy;
    return changed;
}

export function isNullOrWhitespace(value: string | null | undefined): boolean {
    return (typeof value === "undefined" || value === null || value.trim() === '');
}
export function isNotNullOrWhitespace(value: string | null | undefined): boolean {
    return !isNullOrWhitespace(value);
}

export function isNullOrUndefined(value: any): boolean {
    return (typeof value === "undefined" || value === null);
}
export function isNotNullOrUndefined(value: any): boolean {
    return !isNullOrUndefined(value);
}

export function isThisLocalhost(): boolean {
    const hostname = window.location.hostname.toLowerCase();
    return hostname === 'localhost' || hostname === '127.0.0.1';
}

enum FormattedStringTokenType {
    Invalid,
    String,
    Date,
    Number,
    Currency,
    Percent
}

/*
This matches any *potentially* valid formatted string token
\{\s*[A-Za-z$_][A-Za-z$_0-9]*\s*(:\s*[uldnpc#\.0-9]+(-[A-Za-z0-3]+)?\s*)?\}

\{\s*[A-Za-z$_][A-Za-z$_0-9]*\s*:\s*[uldnpc#\.0-9]+(-[A-Za-z0-3]+)?\s*\}
*/

function getTokenType(token: string): FormattedStringTokenType {
    if(/\{\s*[A-Za-z$_][A-Za-z$_0-9]*\s*(:\s*[uUlL]\s*)?\}/.test(token)) {
        return FormattedStringTokenType.String;
    }
    if(/\{\s*[A-Za-z$_][A-Za-z$_0-9]*\s*:\s*d(-[dwMNYyhHmsa0-3]+)?\s*\}/.test(token)) {
        return FormattedStringTokenType.Date;
    }
    if(/\{\s*[A-Za-z$_][A-Za-z$_0-9]*\s*:\s*n(#|[1-9][0-9]?)(\.(#|[1-9][0-9]?))?\s*\}/.test(token)) {
        return FormattedStringTokenType.Number;
    }
    if(/\{\s*[A-Za-z$_][A-Za-z$_0-9]*\s*:\s*c(#|[1-9][0-9]?)(\.(#|[1-9][0-9]?))?-[A-Za-z]{3}\s*\}/.test(token)) {
        return FormattedStringTokenType.Currency;
    }
    if(/\{\s*[A-Za-z$_][A-Za-z$_0-9]*\s*:\s*p(#|[1-9][0-9]?)(\.(#|[1-9][0-9]?))?\s*\}/.test(token)) {
        return FormattedStringTokenType.Percent;
    }

    return FormattedStringTokenType.Invalid;
}

function parseDateTimeFormatOptions(format: string, options: Intl.DateTimeFormatOptions): string | undefined {

    // year
    const yearMatches = /[yY]{1,4}/g.exec(format);
    if(!!yearMatches) {
        if(yearMatches.length > 1) {
            return 'Invalid date format - multiple year format specifiers';
        }
        const yearSpec = yearMatches[0].toLowerCase();
        if(yearSpec === 'yy') {
            options.year = '2-digit';
        } else if(yearSpec === 'yyyy') {
            options.year = 'numeric';
        } else {
            return 'Invalid date format - year format must be expressed as yy, or yyyy';
        }
    }

    // month
    const monthMatches = /(?:[M]{1,4}|N)/g.exec(format);
    if(!!monthMatches) {
        if(monthMatches.length > 1) {
            return 'Invalid date format - multiple month format specifiers';
        }
        const monthSpec = monthMatches[0];
        if(monthSpec === 'N') {
            options.month = 'narrow';
        } else if(monthSpec === 'M') {
            options.month = 'numeric';
        } else if(monthSpec === 'MM') {
            options.month = '2-digit';
        } else if(monthSpec === 'MMM') {
            options.month = 'short';
        } else if(monthSpec === 'MMMM') {
            options.month = 'long';
        } else {
            return 'Invalid date format - month format must be expressed as M, MM, MMM, MMMM, or N';
        }
    }

    

    // day
    const dayMatches = /[dD]{1,2}/g.exec(format);
    if(!!dayMatches) {
        if(dayMatches.length > 1) {
            return 'Invalid date format - multiple day format specifiers';
        }
        const daySpec = dayMatches[0];
        if(daySpec.length === 1) {
            options.day = 'numeric';
        } else {
            options.day = '2-digit';
        }
    }

    // weekday
    const weekdayMatches = /(?:[wW]{1,3})/g.exec(format);
    if(!!weekdayMatches) {
        if(weekdayMatches.length > 1) {
            return 'Invalid date format - multiple weekday format specifiers';
        }
        const weekdaySpec = weekdayMatches[0].toLowerCase();
        if(weekdaySpec === 'w') {
            options.weekday = 'narrow';
        } else if(weekdaySpec === 'ww') {
            options.weekday = 'short';
        } else if(weekdaySpec === 'www') {
            options.weekday = 'long';
        } else {
            return 'Invalid date format - weekday format must be expressed as w, ww, or www';
        }
    }

    // hour
    const hourMatches = /(?:[h]{1,2}|[H]{1,2})/g.exec(format);
    if(!!hourMatches) {
        if(hourMatches.length > 1) {
            return 'Invalid date format - multiple hour format specifiers';
        }
        const hourSpec = hourMatches[0];
        if(hourSpec === 'h') {
            options.hour = 'numeric';
            options.hour12 = true;
        } else if(hourSpec === 'hh') {
            options.hour = '2-digit';
            options.hour12 = true;
        } else if(hourSpec === 'H') {
            options.hour = 'numeric';
            options.hour12 = false;
        } else if(hourSpec === 'HH') {
            options.hour = '2-digit';
            options.hour12 = false;
        } else {
            return 'Invalid date format - hour format must be expressed as h, H, hh, or HH';
        }
    }

    // day period
    const meridianMatches = /[aA]{1,3}/g.exec(format);
    if(!!meridianMatches) {
        if(meridianMatches.length > 1) {
            return 'Invalid date format - multiple day period format specifiers';
        }
        const meridianSpec = meridianMatches[0];
        if(meridianSpec.length === 3) {
            options.dayPeriod = 'long';
        } else {
            options.dayPeriod = undefined; // no other option will work out as planned
        }
    }

    // minute
    const minuteMatches = /[m]{1,2}/g.exec(format);
    if(!!minuteMatches) {
        if(minuteMatches.length > 1) {
            return 'Invalid date format - multiple minute format specifiers';
        }
        const minuteSpec = minuteMatches[0];
        if(minuteSpec === 'm') {
            options.minute = 'numeric';
        } else if(minuteSpec === 'mm') {
            options.minute = '2-digit';
        } else {
            return 'Invalid date format - minute format must be expressed as m or mm';
        }
    }

    // second
    const secondMatches = /[sS]{1,2}[0-3]*/g.exec(format);
    if(!!secondMatches) {
        if(secondMatches.length > 1) {
            return 'Invalid date format - multiple second format specifiers';
        }
        const secondSpec = secondMatches[0].toLowerCase();
        if(/s[0-3]?/.test(secondSpec)) {
            options.second = 'numeric';
        } else if(/ss[0-3]?/.test(secondSpec)) {
            options.second = '2-digit';
        } else {
            return 'Invalid date format - second format must be expressed as s[0-3], or ss[0-3]';
        }

        if(/[0-3]$/.test(secondSpec)) {
            const fsd = parseInt(secondSpec[secondSpec.length - 1], 10);
            if(fsd > 0) {
                options.fractionalSecondDigits = fsd as 1 | 2 | 3;
            }
        }
    }

    return undefined;
}

function resolveDate(token: string, locale: string, value: unknown, result: IFormatStringResult) {
    let format = token.replace('}', '').split(':')[1].trim();
    let options: Intl.DateTimeFormatOptions = {};

    if(format.indexOf('-') !== -1) {
        format = format.split('-')[1];
        const optionsResult = parseDateTimeFormatOptions(format, options);
        if(optionsResult !== undefined) {
            result.success = false;
            result.error = optionsResult as string;
            return;
        }
    } else { // use default for when no explicit date/time format provided
        options = {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
        };
    }

    try {
        const resolvedValue = (new Intl.DateTimeFormat(locale, options)).format(value as Date);
        result.formattedString = result.formattedString.replace(token, resolvedValue);
    } catch {
        result.success = false;
        result.error = `Value corresponding to token ${token} is not a Date or valid number`;
    }
}

function parseNumericalOptions(format: string, options: Intl.NumberFormatOptions) {
    const fmtParts = format.substring(1).split('.');
    const fmtLeft = fmtParts[0];
    const fmtRight = fmtParts.length === 2 ? fmtParts[1] : undefined;

    let minIntDigits = parseInt(fmtLeft, 10);
    if(isNaN(minIntDigits) === false) {
        minIntDigits = minIntDigits <= 21 ? minIntDigits : 21;
        options.minimumIntegerDigits = minIntDigits;
    } else {
        options.minimumIntegerDigits = 1;
    }

    if(fmtRight === undefined) {
        options.maximumFractionDigits = 0;
    } else {
        const decDigits = parseInt(fmtRight, 10);
        if(isNaN(decDigits) === false) {
            options.minimumFractionDigits = decDigits;
            options.maximumFractionDigits = decDigits;
            
            // we have to do this to defeat a very annoying TypeScript bug
            const opts = options as any;
            opts.roundingMode = 'halfCeil';
        }
    }
}

function resolveNumerical(token: string, format: string, locale: string, value: unknown, options: Intl.NumberFormatOptions, result: IFormatStringResult) {
    parseNumericalOptions(format, options);

    const valueIsANumber = isNaN(parseFloat(value as string)) === false;
    if(valueIsANumber) {
        const resolvedValue = (new Intl.NumberFormat(locale, options)).format(value as number);
        result.formattedString = result.formattedString.replace(token, resolvedValue);
    } else {
        result.success = false;
        result.error = `Value corresponding to token ${token} is not a number`;
    }
}

function resolveNumber(token: string, locale: string, value: unknown, result: IFormatStringResult) {
    const format = token.replace('}', '').split(':')[1].trim();
    const options: Intl.NumberFormatOptions = {
        style: 'decimal'
    };
    
    resolveNumerical(token, format, locale, value, options, result);
}

function resolvePercent(token: string, locale: string, value: unknown, result: IFormatStringResult) {
    const format = token.replace('}', '').split(':')[1].trim();
    const options: Intl.NumberFormatOptions = {
        style: 'percent'
    };
    
    resolveNumerical(token, format, locale, value, options, result);
}

function resolveCurrency(token: string, locale: string, value: unknown, result: IFormatStringResult) {
    const currencyFormat = token.replace('}', '').split(':')[1].trim();
    const formatParts = currencyFormat.split('-');
    const options: Intl.NumberFormatOptions = {
        style: 'currency',
        currency: formatParts[1]
    };
    
    resolveNumerical(token, formatParts[0], locale, value, options, result);
}

function resolveString(token: string, locale: string, value: any, result: IFormatStringResult) {
    let formatParts = token.replace('}', '').split(':');
    let resolvedValue = value.toString() as string;

    // uses upper/lower case indicator
    if(formatParts.length > 1) {
        const isUpperCase = formatParts[1].trim().toLowerCase() === 'u';
        resolvedValue = isUpperCase ? resolvedValue.toLocaleUpperCase(locale)
        : resolvedValue.toLocaleLowerCase(locale);
    }
    
    result.formattedString = result.formattedString.replace(token, resolvedValue);
}

/**
 * Returns template after the first token with the given name has been replaced with the given value
 * @param template 
 * @param tokenName 
 * @param value 
 */
function resolveToken(tokenName: string, locale: string, value: unknown, result: IFormatStringResult) : IFormatStringResult {
    //check validity of teken name
    if(/[A-Za-z$_][A-Za-z$_0-9]*/.test(tokenName) === false) {
        result.success = false;
        result.error = `Encountered invalid token name '${tokenName}'`;
        return result;
    }

    const test = new RegExp(`\\{\\s*${tokenName}\\s*(:\\s*[uldnpc#\\.0-9]+(-[A-Za-z0-3]+)?\\s*)?\\}`);
    const match = test.exec(result.formattedString);
    // nothing to do if no matches found
    if(!match || match.length === 0) { return result; }

    const token = match[0];
    const tokenType = getTokenType(token);

    // null/undefined value will replace any token with an empty string, regardless of its formatting specification
    if(isNullOrUndefined(value)) {
        result.formattedString = result.formattedString.replace(token, '');
        return result;
    }

    switch(tokenType) {
        case FormattedStringTokenType.Currency:
            resolveCurrency(token, locale, value, result);
            break;
        case FormattedStringTokenType.Date:
            resolveDate(token, locale, value, result);
            break;
        case FormattedStringTokenType.Number:
            resolveNumber(token, locale, value, result);
            break;
        case FormattedStringTokenType.Percent:
            resolvePercent(token, locale, value, result);
            break;
        case FormattedStringTokenType.String:
            resolveString(token, locale, value, result);
            break;
        default: // invalid format
            result.success = false;
            result.error = `Encountered problem with token ${token} - invalid format specifier`;
            break;
    }

    return result;
}

/**
 * Creates a string based on the given template, where named placeholders (tokens) within the template are substituted with values from the given
 * value object. Optional formatting may be applied to these options based on the given locale
 * 
 * **Formatting Options**
 * 
 * String
 * {tokenName} - value as string
 * {tokenName:u} - value as uppercase string (using the given or default locale)
 * {tokenName:l} - value as lowercase string (using the given or default locale)
 * 
 * Date/Time
 * {tokenName:d} - date with default components dMyyyy (see below for details)
 * {tokenName:d-ddMMyyyyhhmmss2} - date and/or with desired date/time components specified as follows:
 * d - date (one or two digits)
 * dd - guaranteed two-digit date (01)
 * w - single character weekday name (W)
 * ww - abbreviated weekday name (Wed)
 * www - weekday name (Wednesday)
 * M - month number (one or two digits)
 * MM - guaranteed two-digit month number (01)
 * N - single character month name (J)
 * MMM - abbreviated month name (Jan)
 * MMMM - full month name (January)
 * yy - 2-digit year
 * yyyy - full year
 * h - 12-hour hour (one or two digits)
 * hh - guaranteed two-digit 12-hour hour
 * H - 24 hour hour
 * HH - guaranteed two-digit 24-hour hour
 * a or A - narrow day period indicator (a or p)
 * aa or AA - short day period indicator (am or pm)
 * aaa or AAA - long day period indicator ('in the morning' or 'in the evening')
 * m - minute (one or two digits)
 * mm - guaranteed two-digit minute
 * s - second (one or two digits)
 * ss - guaranteed two-digit second
 * s[number] - seconds, with [number] fractional digits (s2 => 1.00, ss1 => 01.0).  Max 3 digits
 * 
 * Number
 * {tokenName:n#} - locale formatted integer (no minimum number of digits)
 * {tokenName:n3} - locale formatted integer, with 3-digit minimum (001). Values 1-21 may be used
 * {tokenName:n#.#} - locale formatted decimal number (no restraint on the number of fractional digits)
 * {tokenName:n#.3} - locale formatted decimal number, with exactly 3 fractional digits (1.000). Values 0 (same as integer) - 99 may be used
 * {tokenName:n2.3} - the concepts described above may be used in conjunction
 * When needed to conform to the format specification, 'halfCeil' rounding is used
 * (see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#options)
 * 
 * Percent
 * {tokenName:p#} - locale formatted percent value. Number formatting same as described above
 * 
 * Currency
 * {tokenName:c#.2-USD} - locale formatted currency value. Number formatting same as described above. Currency type specification is required
 * 
 * @param template String template containing named tokens to be substituted with values
 * @param values Object containing named values to insert into the string template
 * @param locale Optional locale to use when formatting values inserted in the output string.  When not specified, the brower's locale is used by default
 * @returns An object specifying the result of the formatting operation, and if successful, the resulting string
 */
export function formatString(template: string, values:ObjectMap<unknown>, locale?: string): IFormatStringResult {
    locale = isNotNullOrWhitespace(locale) ? locale as string : window.navigator.language;
    const lce = isNotNullOrWhitespace(locale) ? locale as string : window.navigator.language;

    const result: IFormatStringResult = {
        formattedString: template,
        success: true,
    };

    forEach(values, (value, key) => {
        let previousResult = result.formattedString;
        while(resolveToken(key!, lce, value, result).formattedString !== previousResult) {
            previousResult = result.formattedString;
            if(result.success === false) {
                return false; // break foreach
            }
        }
    });

    return result;
}
